import { Calendar, Mail, Phone, Star, User } from "lucide-react";
import { DateTime } from "luxon";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PriceDisplay } from "@/components/ui/price-display";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";

interface CustomerDetailDialogProps {
  customer: CustomerDetailFragment;
  onClose: () => void;
  onEdit?: (customer: CustomerDetailFragment) => void;
}

export function CustomerDetailDialog({ customer, onClose, onEdit }: CustomerDetailDialogProps) {
  const formatDate = (dateString: string) => {
    return timezoneUtils.fromISO(dateString).toLocaleString();
  };

  // dateOfBirth is a Postgres DATE column but serialized as full ISO with Z;
  // projecting to salon TZ would shift the displayed day for salons west of
  // UTC, so keep the UTC-zone DateTime here.
  const formatDateOfBirth = (dateString: string) => {
    return DateTime.fromISO(dateString).toFormat("dd/MM/yy");
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Customer Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Customer Profile */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(customer.firstName, customer.lastName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg">
                  {customer.firstName} {customer.lastName}
                </h3>
                {customer.currentTier && (
                  <Badge
                    variant="secondary"
                    className="mt-1"
                    style={{
                      backgroundColor: `${customer.currentTier.color}20`,
                      color: customer.currentTier.color,
                      borderColor: `${customer.currentTier.color}40`,
                    }}
                  >
                    <Star className="w-3 h-3 mr-1" />
                    {customer.currentTier.name}
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-2 pl-15">
              {customer.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{customer.email}</span>
                </div>
              )}
              {customer.phoneNumbers && customer.phoneNumbers.length > 0 && (
                <div className="space-y-1">
                  {customer.phoneNumbers
                    .filter((phone) => phone.isActive)
                    .map((phone) => (
                      <div key={phone.id} className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{phone.phone}</span>
                        {phone.isPrimary && (
                          <Badge variant="secondary" className="text-xs">
                            Primary
                          </Badge>
                        )}
                        {phone.label && (
                          <span className="text-xs text-muted-foreground">({phone.label})</span>
                        )}
                      </div>
                    ))}
                </div>
              )}
              {customer.dateOfBirth && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">
                    Birthday: {formatDateOfBirth(customer.dateOfBirth)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Customer Statistics */}
          <div className="space-y-2">
            <h4 className="text-base font-medium">Customer Stats</h4>
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-md">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Total Visits</Label>
                <div className="text-xl font-bold">{customer.totalVisits}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Total Spent</Label>
                <div className="text-xl font-bold text-green-600 dark:text-green-400">
                  <PriceDisplay amount={customer.totalSpent} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Average Spent</Label>
                <div className="text-base font-semibold">
                  <PriceDisplay amount={customer.averageSpent} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Loyalty Points</Label>
                <div className="text-base font-semibold text-primary">{customer.totalPoints}</div>
              </div>
            </div>
          </div>

          {/* Last Visit */}
          {customer.lastVisitDate && (
            <div className="space-y-2">
              <h4 className="text-base font-medium">Last Visit</h4>
              <div className="text-sm p-2 bg-muted/30 rounded">
                {formatDate(customer.lastVisitDate)}
              </div>
            </div>
          )}

          {/* Notes */}
          {customer.notes && (
            <div className="space-y-2">
              <h4 className="text-base font-medium">Notes</h4>
              <div className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded">
                {customer.notes}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between pt-4 border-t">
          {onEdit && (
            <Button variant="outline" onClick={() => onEdit(customer)}>
              Edit Customer
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
